import { useState } from 'react';
import { Users, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { IdentityConflict } from '@/lib/roundSync';

interface Props {
  conflicts: IdentityConflict[];
  onResolve: (resolutions: Record<string, string>) => void;
  onDismiss: () => void;
  isRetrying: boolean;
}

function conflictKey(c: IdentityConflict): string {
  return `${c.playerTeam ?? 'X'}-${c.playerPosition ?? 'X'}`;
}

function candidateLabel(
  c: { personId: string; claimedUserId: string | null },
  idx: number,
  total: number
): string {
  const accountTag = c.claimedUserId ? 'linked account' : 'guest';
  return total === 1 ? `(${accountTag})` : `Option ${idx + 1} — ${accountTag}`;
}

export function AmbiguousIdentityModal({ conflicts, onResolve, onDismiss, isRetrying }: Props) {
  const [selections, setSelections] = useState<Record<string, string>>({});

  const allResolved = conflicts.every(c => selections[conflictKey(c)]);

  const handleSelect = (key: string, personId: string) => {
    setSelections(prev => ({ ...prev, [key]: personId }));
  };

  const handleConfirm = () => {
    if (!allResolved) return;
    onResolve(selections);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/60" data-testid="modal-ambiguous-identity">
      <div className="bg-white rounded-t-2xl max-h-[90dvh] overflow-y-auto">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        <div className="px-5 pb-2 pt-1">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <h2 className="text-base font-bold text-gray-900">Who did you mean?</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            {conflicts.length === 1
              ? 'One player name matches multiple people in your group. Pick the right one to save this round.'
              : `${conflicts.length} player names each match multiple people in your group. Pick the right person for each.`}
          </p>
        </div>

        <div className="px-5 pb-4 flex flex-col gap-5">
          {conflicts.map((conflict) => {
            const key = conflictKey(conflict);
            const selected = selections[key];

            return (
              <div key={key} className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-800">
                    You added: <span className="text-[#064E3B]">{conflict.displayName}</span>
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  We found {conflict.candidates.length} existing players with this name in your group. Which one?
                </p>
                <div className="flex flex-col gap-2">
                  {conflict.candidates.map((candidate, idx) => {
                    const isSelected = selected === candidate.personId;
                    return (
                      <button
                        key={candidate.personId}
                        type="button"
                        data-testid={`btn-candidate-${key}-${idx}`}
                        onClick={() => handleSelect(key, candidate.personId)}
                        className={[
                          'w-full text-left px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors',
                          isSelected
                            ? 'border-[#064E3B] bg-[#064E3B] text-white'
                            : 'border-gray-300 bg-white text-gray-700 active:bg-gray-50',
                        ].join(' ')}
                      >
                        <span className="font-semibold">{conflict.displayName}</span>
                        <span className={['ml-2 text-xs font-normal', isSelected ? 'text-emerald-200' : 'text-gray-400'].join(' ')}>
                          {candidateLabel(candidate, idx, conflict.candidates.length)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="px-5 pb-6 flex flex-col gap-3">
          <Button
            onClick={handleConfirm}
            disabled={!allResolved || isRetrying}
            className="w-full bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15] font-semibold h-12"
            data-testid="btn-conflict-confirm"
          >
            {isRetrying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving round…
              </>
            ) : (
              'Confirm & save round'
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={onDismiss}
            disabled={isRetrying}
            className="w-full text-gray-500 h-10"
            data-testid="btn-conflict-dismiss"
          >
            Keep playing offline
          </Button>
        </div>
      </div>
    </div>
  );
}
